import {
  Alert,
  Checkbox,
  DatePicker,
  Divider,
  Input,
  Modal,
  Pagination,
  PaginationProps,
  Popover,
  Select,
  Spin,
  Table,
  TableColumnsType,
  Tooltip,
} from "antd";
import { FC, useEffect, useRef, useState } from "react";
import {
  BsArrowDownShort,
  BsArrowUpShort,
  BsEraserFill,
  BsFilterRight,
} from "react-icons/bs";
import { AnyObject } from "yup";
import { ActionsKey } from "../../models/Actions";
import { ColumnItem } from "../../models/table";
import { LoadingOutlined } from "@ant-design/icons";
import { HiEye, HiPencil } from "react-icons/hi";
import { IoTrashOutline } from "react-icons/io5";
import { Http } from "../../../utilities/axios/http";
import { API_BASE_URL } from "../../../utilities/API_CONSTANT";
import "./style.scss";
import { Assets } from "../../../assets";
import { InputSelect } from "../InputSelect/InputSelect";
import dayjs from "dayjs";

type ChangePageType = {
  itemsPerPage: number;
  page: number;
};

type PaginationType = {
  defaultCurrentPage?: number;
  totalItems?: number;
  itemsPerPage?: number;
};

type Other = {
  onClick?: Function;
  id?: number | string;
  row?: any;
};

type LFTableProps = {
  columns: ColumnItem[];
  data: AnyObject[];
  onAction: (item: any, action: ActionsKey) => void;
  sort?: { dataKey: string; order?: "asc" | "desc" } | null;
  onSearch?: (search: { searchStr: string; dataKey: string }) => void;
  onSort?: (order: { dataKey: string; order?: "asc" | "desc" } | null) => void;
  onResetSearch: () => void;
  onChangePage: (e: ChangePageType) => void;
  pagination?: PaginationType;
  params?: any;
  isLoading?: boolean;
  otherActions?: FC<Other>[];
  isCanShow?: (row?: any) => boolean;
  isCanEdit?: (row?: any) => boolean;
  isCanDelete?: (row?: any) => boolean;
  exportPdfUrl?: string;
  includeColumnsInExport?: boolean;
  fileName?: string;
};

export const LFTableV3 = ({
  columns,
  data,
  onAction,
  onSearch,
  onSort,
  onResetSearch,
  pagination,
  onChangePage,
  params,
  isLoading,
  otherActions,
  isCanEdit,
  isCanShow,
  isCanDelete,
  exportPdfUrl,
  includeColumnsInExport = false,
  fileName,
}: LFTableProps) => {
  const searchRefs: any[] = [];

  const getDefaultSort = () => {
    const col = columns.find((c) => c.defaultSort);
    if (!col) return null;
  
    return {
      dataKey: col.sortKey || col.dataIndex || col.nestedKey || "",
      order: col.defaultSort,
    };
  };

  const [openCol, setOpenCol] = useState<boolean>(false);
  const [timeoutId, setTimeoutId] = useState<any>(null);
  const [sort, setSort] = useState<any>(null);
  const lastSortedKeyRef = useRef<string | null>(null);
  const [isHavingSearch, setIsHavingSearch] = useState<boolean>(false);
  const [isOpenDelete, setIsOpenDelete] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<boolean>(false);
  const [visibleCols] = useState(columns);
  const [isDownloading, setIsDownloading] = useState(false);

  const [virtualParams, setVirtualParams] = useState<any>(params);

  let cols: ColumnItem[] = columns;

  useEffect(() => {
    setVirtualParams(params);
  }, [params]);

  useEffect(() => {
    setIsHavingSearch(
      cols.some((item) => item.searchType !== "none" && item.searchType)
    );
  }, [cols]);

  useEffect(() => {
    const defaultSort = getDefaultSort();

  const isDefaultCol = defaultSort?.dataKey === lastSortedKeyRef.current;

  // fallback if not the default valued column
  if (!sort && defaultSort && !isDefaultCol) {
    setSort(defaultSort);
    return;
  }
    
    const order = {
      ...params,
      ...(sort
        ? {
            order: {
              dataKey: sort.sortKey || sort.dataKey, // Utiliser sortKey si défini
              order: sort.order,
            },
          }
        : {}),
    };

    onSort && onSort(formatParams(order));
  }, [sort]);

  /**
   * Search logic
   * Wait 500ms after typing before call API
   */

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  const onTypingOnSearch = (value: any) => {
    setVirtualParams((prev: any) => ({
      ...prev,
      page: 1,
      ...value,
    }));
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const id = setTimeout(() => {
      onSearch && onSearch({ ...params, page: 1, ...value });
    }, 500);
    setTimeoutId(id);
  };

  /**End Search logic */

  /**Export Pdf logic */
  const downloadPDF = async (fileName: string) => {
    if (!exportPdfUrl) return;

    let fullUrl = `${API_BASE_URL}${exportPdfUrl}`;

    if (includeColumnsInExport) {
      const selectedCols = visibleCols
        .filter((col) => col.show)
        .map((col) => col.nestedKey || col.dataIndex)
        .filter(Boolean);

      const query = new URLSearchParams();
      selectedCols.forEach((key) => {
        const parts = key.split(".");
        if (parts.length === 1) {
          query.append("p[]", parts[0]);
        } else if (parts.length === 2) {
          query.append(`p[${parts[0]}][]`, parts[1]);
        } else if (parts.length === 3) {
          query.append(`p[${parts[0]}][${parts[1]}][]`, parts[2]);
        }
      });

      fullUrl += (fullUrl.includes("?") ? "&" : "?") + query.toString();
    }

    try {
      setIsDownloading(true);
      const res = await Http.getFile(fullUrl);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const providedName = res?.headers["content-disposition"]
      ?.replace('"', "")
      ?.split("filename=")[1]
      ?.replace(".pdf", "")
      ?.trim();
      a.href = url;
      a.download = fileName ? `${fileName}.pdf` : providedName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erreur téléchargement PDF:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  // rendu export bouton
  const renderExportBtn = (fileName: string) => {
    if (!exportPdfUrl) return null;

    return (
      <div className="text-end pt-2 mb-2 me-5 cursor-pointer d-flex w-100 justify-content-end">
        <div
          className=""
          style={{
            width: "50px",
          }}
        >
          <Spin
            indicator={<LoadingOutlined spin />}
            size="small"
            spinning={isDownloading}
          >
            <Tooltip title="Export PDF">
              <img
                alt="export_pdf"
                className="icon-export__image"
                src={Assets.icons.png.pdf}
                onClick={() => downloadPDF(fileName)}
              />
            </Tooltip>
          </Spin>
        </div>

        {/* {isDownloading ? (
          <Spin size="small" />
        ) : (
          <img
            src={Assets.icons.png.pdf}
            onClick={() => downloadPDF(fileName)}
            className="extract-icon"
            title="Télécharger PDF"
            width={"45px"}
            height={"50px"}
          />
        )} */}
      </div>
    );
  };
  /**End export Pdf logic */

  const formatParams = (defaultParams: any) => {
    const obj: any = { ...defaultParams, order: null };

    Object.keys(defaultParams).forEach((key) => {
      if (key?.includes("order")) {
        delete obj[key];
      }
    });
    Object.keys(defaultParams).forEach((key: any) => {
      if (key === "order") {
        obj["order[" + (defaultParams?.order?.dataKey ?? "") + "]"] =
          defaultParams?.order?.order;
      } else {
        obj[key] = obj[key]?.toString();
      }
    });
    delete obj["order"];
    return obj;
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpenCol(newOpen);
  };

  const handleShowModalDelete = () => {
    setIsOpenDelete((prev: boolean) => !prev);
  };

  const onClickDeleteHandler = (item: any) => {
    setSelectedItem(item);
    handleShowModalDelete();
  };

  const showTotal: PaginationProps["showTotal"] = (total: number) =>
    `Total ${total}`;

  const withDefaultRender = (columns: any[]) => {
    return columns.map((col) => {
      if (!col.render) {
        return {
          ...col,
          //render: (value: any) => (value ?? "-") || "-",
          render: (value: any) => value ?? "-"
        };
      }
      return col;
    });
  };

  /**
   * Columns
   */
  const col2show: TableColumnsType<AnyObject> = [
    {
      key: 0,
      title: () => (
        <div className="">
          {isHavingSearch ? (
            <div
              style={{ height: 31, width: 20, minWidth: "30px" }}
              className="p-1 d-flex justify-content-start px-2"
            >
              <Tooltip title={"Effacer"}>
                <div
                  className=" cursor-pointer "
                  onClick={() => {
                    searchRefs?.forEach((input) => {
                      if (input && input?.current) {
                        input.current.value = "";
                      } else if (input && input?.value) {
                        input.value = "";
                      }
                    });
                    if (onResetSearch) {
                      onResetSearch();
                    }
                  }}
                >
                  <BsEraserFill size={18} />
                </div>
              </Tooltip>
            </div>
          ) : (
            <div></div>
          )}
          <div className="d-flex justify-content-center align-items-center justify-content-between p-2">
            <Tooltip title={"Afficher/Cacher"}>
              <Popover
                open={openCol}
                trigger={"click"}
                placement="bottomLeft"
                onOpenChange={handleOpenChange}
                content={() => {
                  let allChecked: boolean = true;
                  cols?.forEach((cd) => {
                    if (!cd?.show) {
                      allChecked = false;
                    }
                  });
                  return (
                    <div>
                      <div>
                        <div>
                          <Checkbox
                            checked={allChecked}
                            onChange={(e) => {
                              let newCols = [...cols];
                              if (allChecked) {
                                newCols.forEach((ne, ie) => {
                                  newCols[ie].show = false;
                                });
                              } else {
                                newCols.forEach((ne, ie) => {
                                  newCols[ie].show = true;
                                });
                              }
                              cols = newCols;
                              setOpenCol(false);
                            }}
                          >
                            <span className="ms-2">Tout</span>
                          </Checkbox>
                        </div>
                      </div>
                      <Divider className="my-2" />
                      {cols?.map((cop: any, ik: any) => {
                        return (
                          <div className="d-flex">
                            <Checkbox
                              checked={cop?.show}
                              onChange={(e) => {
                                let newCols = [...cols];
                                newCols[ik].show = !cols[ik].show;
                                cols = newCols;
                                setOpenCol(false);
                              }}
                            >
                              <span className="ms-2">{cop?.title}</span>
                            </Checkbox>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              >
                <div
                  className="p-0 cursor-pointer"
                  // onClick={() => setOpenCol(true)}
                >
                  <BsFilterRight size={20} />
                </div>
              </Popover>
            </Tooltip>
          </div>
        </div>
      ),
      render: () => (
        <div
          className=""
          style={{
            minHeight: "45px",
          }}
        ></div>
      ),
      fixed: true,
      width: 50,
    },
    !otherActions?.length && !isCanShow && !isCanEdit && !isCanDelete
      ? {}
      : {
          key: "",
          title: () => {
            return (
              <div className="">
                {isHavingSearch && (
                  <div
                    className="d-flex  justify-content-between p-2 w-100"
                    style={{
                      height: "37px",
                    }}
                  ></div>
                )}
                <div className="d-flex  justify-content-between p-2 w-100">
                  <div className="w-full"></div>
                  <div className="w-full"></div>
                  <div className="w-full">Actions</div>
                  <div className="w-full"></div>
                  <div className="w-full"></div>
                </div>
              </div>
            );
          },
          render: (role, row, i) => {
            // if(!otherActions?.length && !isCanShow && !isCanEdit && !isCanDelete){
            //   return
            // }
            return (
              <div
                className={`
                 d-flex   align-items-center  px-4 gap-3 h-100 text-center
                ${i % 2 !== 0 ? "table-row-c2 " : "table-row-c1"}
                `}
                style={{
                  backgroundColor: "inherit",
                  marginLeft: "25px",
                }}
              >
                {isCanShow && isCanShow(row) && (
                  <Tooltip title={"Détails"}>
                    <div
                      className=" cursor-pointer"
                      onClick={() => {
                        onAction(row, "read");
                      }}
                    >
                      <HiEye style={{ width: "17.5px", height: "17.5px" }} />
                    </div>
                  </Tooltip>
                )}
                {isCanEdit && isCanEdit(row) && (
                  <Tooltip title={"Modifier"}>
                    <div
                      className=" cursor-pointer"
                      onClick={() => {
                        onAction(row, "update");
                      }}
                    >
                      <HiPencil style={{ width: "17.5px", height: "17.5px" }} />
                    </div>
                  </Tooltip>
                )}
                {isCanDelete && isCanDelete(row) && (
                  <Tooltip title={"Supprimer"}>
                    <div
                      className=" cursor-pointer"
                      onClick={() => {
                        onClickDeleteHandler(row);
                      }}
                    >
                      <IoTrashOutline style={{ width: "17.5px", height: "17.5px" }} />
                    </div>
                  </Tooltip>
                )}

                {otherActions &&
                  Array.isArray(otherActions) &&
                  otherActions.map((Other) => (
                    <div className="cursor-pointer d-flex align-items-center other-action-icon">
                      <Other id={(row as any)?.id} row={row}/>
                    </div>
                  ))}
              </div>
            );
          },
          fixed: true,
          width: 150,
        },
  ];
  cols.forEach((col: ColumnItem) => {
    if (col?.show) {
      col2show.push({
        ...col,
        title: () =>
          // item
          {
            return (
              <div
                className={`
                  d-flex flex-column align-items-center
                  ${col?.isCanSort ? "cursor-pointer" : ""}
                  `}
              >
                {isHavingSearch ? (
                  <div className="w-100 p-1">
                    {col?.searchType && col?.searchType === "text" && (
                      <>
                        <Input
                          value={
                            //virtualParams?.[col?.nestedKey ?? col?.dataIndex]
                            virtualParams?.[
                              col?.searchKey ??
                              col?.nestedKey ??
                              col?.dataIndex
                            ] ?? ""
                          }
                          style={{
                            height: 29,
                            color: "#3f3e3e",
                            fontSize: "13px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          onChange={(e) => {
                            if (e.target.value?.length && onSearch) {
                              let theKeyUse = "";
                              if (col?.nestedKey) {
                                let arr = col?.nestedKey?.split(".");
                                arr?.forEach((nest: any, ic: any) => {
                                  if (ic === 0) {
                                    theKeyUse += nest;
                                  } else {
                                    theKeyUse += "[" + nest + "]";
                                  }
                                });
                              } else {
                                theKeyUse = col?.dataIndex;
                              }
                              onTypingOnSearch({
                                [col?.searchKey ?? theKeyUse]: e.target.value,
                              });
                            } else if (
                              e.target.value?.length === 0 &&
                              onSearch
                            ) {
                              onTypingOnSearch({
                                [col?.searchKey ??
                                col?.nestedKey ??
                                col?.dataIndex]: "",
                              });
                            }
                          }}
                        />
                      </>
                    )}
                    {col?.searchType === "select" && (
                      <>
                        {col?.selectUri ? (
                          <InputSelect
                            className="w-100 custom-small-select"
                            uri={col?.selectUri}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            style={{
                              height: 29,
                              fontSize: "13px",
                              color: "#3f3e3e",
                              backgroundColor: "#fcfcfc",
                            }}
                            valueOption={{
                              labelKey: (item: any) => item?.abbreviation ?? item?.name,
                              valueKey: "id",
                            }}
                            params={col?.selectParams}
                            value={params?.[col?.nestedKey ?? col?.dataIndex]}
                            allowClear
                            onChange={(val) => {
                              const v = val?.value;
                              let theKeyUse =
                                col?.nestedKey ?? col?.dataIndex ?? "";

                              if (onSearch) {
                                onTypingOnSearch({
                                  [theKeyUse]: v,
                                });
                              }
                            }}
                          />
                        ) : (
                          <Select
                            className="w-100 custom-small-select"
                            style={{
                              height: 29,
                              fontSize: "13px",
                              color: "#3f3e3e",
                              backgroundColor: "#fcfcfc",
                            }}
                            size="large"
                            options={col?.searchOptions ?? []}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            value={params?.[col?.nestedKey ?? col?.dataIndex]}
                            allowClear
                            showSearch
                            loading={col?.isLoading}
                            onChange={(v) => {
                              let theKeyUse =
                                col?.nestedKey ?? col?.dataIndex ?? "";

                              if (onSearch) {
                                onTypingOnSearch({
                                  [theKeyUse]: v,
                                });
                              }
                            }}
                          />
                        )}
                      </>
                    )}
                    {col?.searchType === "date" && (
                      <DatePicker
                        placeholder=""
                        showNow={false}
                        format={"DD/MM/YYYY"}
                        allowClear
                        className="w-100"
                        value={
                          virtualParams?.[`${col?.searchKey ?? col?.dataIndex}[after]`]
                            ? dayjs(
                                virtualParams?.[`${col?.searchKey ?? col?.dataIndex}[after]`]
                              )
                            : undefined
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onChange={(date) => {
                          const key = col?.searchKey ?? col?.dataIndex;

                          if (!date) {
                            onTypingOnSearch({
                              [`${key}[after]`]: "",
                              [`${key}[before]`]: "",
                            });
                            return;
                          }

                          const day = date.format("YYYY-MM-DD");

                          onTypingOnSearch({
                            [`${key}[after]`]: `${day} 00:00:00`,
                            [`${key}[before]`]: `${day} 23:59:59`,
                          });
                        }}
                      />
                    )}
                    {!col?.searchType || col?.searchType === "none" ? (
                      <div className="w-100" style={{ height: 31 }}></div>
                    ) : (
                      ""
                    )}
                  </div>
                ) : (
                  ""
                )}
                <div className="p-1 table-title text-center w-100 h-100 d-flex align-items-center justify-content-center"
                {...(col?.isCanSort
                  ? {
                      /* onClick: () => {
                        if (!sort && onSort) {
                          setSort({
                            dataKey: col.sortKey || col.dataIndex || col?.nestedKey || "",
                            order: "desc",
                          });
                        } else if (
                          sort &&
                          sort?.dataKey !== (col.sortKey || col?.dataIndex) &&
                          onSort
                        ) {
                          setSort({
                            dataKey: col.sortKey || col.dataIndex || col?.nestedKey || "",
                            order: "desc",
                          });
                        } else if (sort?.order === "asc" && onSort) {
                          setSort(null);
                        } else if (sort?.order === "desc" && onSort) {
                          setSort({
                            dataKey: col.sortKey || col.dataIndex || col?.nestedKey || "",
                            order: "asc",
                          });
                        }
                      }, */
                      onClick: () => {
                        const currentKey = col.sortKey || col.dataIndex || col?.nestedKey || "";
                        const defaultSort = getDefaultSort();
                        const isDefaultCol = defaultSort?.dataKey === currentKey;
                      
                        lastSortedKeyRef.current = currentKey;

                        if (!sort) {
                          setSort({
                            dataKey: currentKey,
                            order: isDefaultCol ? defaultSort.order : "desc",
                          });
                          return;
                        }
                      
                        if (sort.dataKey !== currentKey) {
                          setSort({
                            dataKey: currentKey,
                            order: "desc",
                          });
                          return;
                        }

                        if (sort.order === "desc") {
                          setSort({
                            dataKey: currentKey,
                            order: "asc",
                          });
                          return;
                        }
                      
                        if (sort.order === "asc") {
                          if (isDefaultCol) {
                            setSort({
                              dataKey: currentKey,
                              order: "desc",
                            });
                          } else {
                            setSort(null);
                          }
                          return;
                        }
                      }
                    }
                  : {})}>
                  <span>{col?.title}</span>
                  {sort?.dataKey === col?.nestedKey ||
                  sort?.dataKey === col?.dataIndex ||
                  sort?.dataKey === col?.sortKey ? (
                    <div>
                      {sort?.order === "asc" && <BsArrowUpShort />}
                      {sort?.order === "desc" && <BsArrowDownShort />}
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            );
          },
      });
    }
  });
  return (
    <div className="table__wrapper">
      {isLoading ? (
        <div className="">
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>
      ) : (
        ""
      )}
      {renderExportBtn(fileName ? fileName : "")}
      <div className="table">
        <Table
          columns={withDefaultRender(
            col2show?.filter((item: any) => item?.title)
          )}
          dataSource={data}
          className=""
          rowClassName={(_, i) => {
            return i % 2 !== 0
              ? "text-center  table-row-c2"
              : "text-center  table-row-c1";
          }}
          locale={{
            emptyText: "Aucune donnée",
          }}
          scroll={{ x: "max-content" }}
          pagination={false}
          rowKey="id"
        />
      </div>
      {pagination ? (
        <div className="table__pagination">
          <Pagination
            className="w-full"
            showSizeChanger
            showTotal={showTotal}
            defaultCurrent={1}
            current={pagination?.defaultCurrentPage}
            pageSize={pagination?.itemsPerPage || 10}
            total={pagination?.totalItems || 0}
            onChange={(page, itemsPerPage) => {
              onChangePage &&
                onChangePage({
                  ...params,
                  page: page ?? 1,
                  itemsPerPage: itemsPerPage,
                });
            }}
          />
        </div>
      ) : (
        ""
      )}

      <Modal
        title={"Suppression"}
        open={isOpenDelete}
        onOk={() => {
          onAction(selectedItem, "delete");
          handleShowModalDelete();
        }}
        onCancel={handleShowModalDelete}
        okText={"Ok"}
        cancelText={"Annuler"}
      >
        <div>
          <Alert
            message={"Voulez-vous vraiment supprimer cet élément ?"}
            type="warning"
            showIcon
          />
        </div>
      </Modal>
    </div>
  );
};
